import Slot = googletag.Slot;
import {LazyLoad} from "./lazyload";
import {ICollection} from "./recursos";
import {EventManager} from "./recursos/event-manager";

declare global {
    interface Window {
        alpred: any;
        pbjs: any;
        googletag: any;
        hbError: boolean;
        adslots: Slot[];
        originalAdSlots: Slot[];
        slots: Promise<string[]>;
        refreshSlot: (id: string, addService: boolean) => Promise<void>;
        tipo_noticia: string;
        tipologia: string;
        device: string;
        entorno: string;
        dominio: string;
        subtipologia: string;
        cmp_value: string;
    }
}

export class PublicidadController {

    /* INSTANCE */
    private resources: HTMLElement[];
    private lazyInstance: LazyLoad;
    private scrollEvent: any;
    private exceptions: string[];
    private readonly temporalExceptions: any;
    private readonly timers: ICollection;
    private readonly executions: ICollection;
    private observer: IntersectionObserver|undefined;
    private max_exec: number = 2;
    private interval_size: number = 30000;
    public static instance: PublicidadController;

    /* STATIC */
    public static async build(): Promise<void> {
        PublicidadController.instance = new this();
    }

    private constructor() {
        this.resources = [];
        this.timers = {};
        this.executions = {};
        this.lazyInstance = new LazyLoad();
        // Estos banners siempre se cargaran al inicio de la pagina
        this.exceptions = ['div-dtop', 'div-dv1', 'div-dh1', 'div-skyleft', 'div-skyright', 'div-mtop', 'div-m1', 'div-inimage'];
        // Nunca se van a cargar estos banners
        this.temporalExceptions = [{ad: 'div-mtop', subtipologia: 'ultima-hora'},
            {ad: 'div-dtop', subtipologia: 'ed-games'},
            {ad: 'div-skyleft', subtipologia: 'ed-games'},
            {ad: 'div-skyright', subtipologia: 'ed-games'},
            {ad: 'div-mtop', subtipologia: 'ed-games'},
            {ad: 'div-mtop', subtipologia: 'home'},
            {ad: 'div-mtop', subtipologia: 'fichajes'},
            {ad: 'div-mtop', subtipologia: 'la-prorroga-de-ed'},
        ];

        window.refreshSlot = this.refreshSlot;

        if (!window.hbError) {
            // Establecemos cada cuantos pixeles se cargan los anuncios
            this.lazyInstance.setPixels(window.tipologia === "noticia"?500:1000);
            this.initLazyAds();
            // Inicializamos el observer para hacer uso de los temporizadores
            this.initObserver();
        }

        googletag.cmd.push(async () => {
            googletag.pubads().enableSingleRequest();
            if (!window.hbError) googletag.pubads().disableInitialLoad();
            googletag.enableServices();

            this.setTargeting();

            if (window.tipologia === "noticia") this.loadInterstitial();

            await this.whenRenderFinish()

            if (window.hbError) {
                for (let [, slot] of Object.entries(window.adslots)) {
                    slot.addService(googletag.pubads());
                }
                window.googletag.pubads().refresh();
            }
        });

        if (window.tipologia === "noticia" || window.tipologia === "video") {
            document.addEventListener('alpred:hb:video_instream_bid_received', async (e: any) => {
                let kvPrebid = "";
                const state = e.detail.state;
                const adServerTargeting = e.detail.data && e.detail.data.adServerTargeting;
                if (state === "bid" && adServerTargeting) {
                    let keyValues = '';
                    for (let key in adServerTargeting) {
                        keyValues += key + '=' + adServerTargeting[key] + '&';
                    }
                    kvPrebid = '&' + keyValues.slice(0, -1);
                }
                const em = EventManager.build();
                em.createCustomEvent(kvPrebid);
                em.dispatchCustomEvent();
            });
        }

        window.alpred.hb.cmd.push(()=> {
            const slots: Slot[] = [];
            for (let [, slot] of Object.entries(window.adslots)) {
                const id = slot.getSlotId().getDomId();
                if (this.exceptions.includes(id) && !this.checkTemporalExceptions(id, window.subtipologia)) {
                    slot.addService(googletag.pubads());
                    slots.push(slot);
                    const item = document.querySelector("#" + id) as HTMLElement;
                    if (id !== "div-sticky" && id !== "div-inimage") {
                        this.executions[id] = 1;
                        this.createTimer(id, item.parentElement as HTMLElement);
                    }
                }
            }

            window.alpred.hb.refresh(slots);

            window.alpred.sticky.cmd.push(()=> {
                window.alpred.sticky.refresh();
            });

            if (window.device === "pc") {
                window.pbjs.onEvent('bidWon', this.checkSkinDesktop);
            }
        });
    }

    private async checkSkinDesktop(e: any): Promise<void> {
        if (e.dealId === 'ramkt') {
            for (let [, slot] of Object.entries(window.adslots)) {
                const id = slot.getSlotId().getDomId();
                if (id === 'div-skyleft' || id === 'div-skyright') {
                    googletag.destroySlots([slot]);
                }
            }
        }
    }

    private async initLazyAds(): Promise<void> {
        const ads = await window.slots;
        const items = document.querySelectorAll('[id^="publicidad"]') as NodeListOf<HTMLElement>;
        this.resources = Array.from(items).filter((item) => {
            return ads.includes(this.getId(item));
        });
        this.resources = this.resources.filter((item) => {
            if (this.lazyInstance.isInViewport(item) || this.exceptions.includes(this.getId(item)) || this.checkTemporalExceptions(this.getId(item), window.subtipologia)) return;
            return item;
        });
        this.scrollEvent = this.lazyAds.bind(this);
        document.addEventListener("scroll", this.scrollEvent);
    }

    private getId(item: HTMLElement): string {
        if (item) {
            if (item.className === "edpixel") {
                return item.id;
            } else {
                if (item.firstElementChild) return item.firstElementChild.id;
            }
        }
        return "";
    }

    private checkTemporalExceptions(ad: string, subtipologia: string): boolean {
        for (let exception of this.temporalExceptions) {
            if (exception.ad === ad && exception.subtipologia === subtipologia) return true;
        }
        return false;
    }

    private async initObserver(): Promise<void> {
        this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const id = this.getId(entry.target as HTMLElement);
                    if (this.timers[id] && this.executions[id] <= this.max_exec) {
                        this.timers[id] = false;
                        this.refreshSlot(id, false);
                        this.createTimer(id, entry.target as HTMLElement);
                    }
                    this.observer?.unobserve(entry.target);
                }
            });
        }, {
            threshold: 1.0
        });
    }

    private async createTimer(id: string, item: HTMLElement): Promise<void> {
        setTimeout(() => {
            this.timers[id] = true;
            this.executions[id] += 1;
            this.observer?.observe(item);
        }, this.interval_size);
    }

    private setTargeting(): void {
        if (window.device && window.device.length > 0) googletag.pubads().setTargeting('platform', window.device === 'pc' ? 'desktop' : 'mobile');
        if (window.entorno === 'test') googletag.pubads().setTargeting('test', 'si');
        if (window.tipologia && window.tipologia.length > 0) {
            googletag.pubads().setTargeting('pagina', window.tipologia);
            googletag.pubads().setTargeting('tipologia', window.tipologia);
            if (window.tipologia === 'noticia') googletag.pubads().setTargeting('tiponoticia', window.tipo_noticia === 'noticia' ? 'standard' : window.tipo_noticia);
        }
        if (window.subtipologia && window.subtipologia.length > 0) {
            googletag.pubads().setTargeting('subtipologia', window.subtipologia);
        }
        googletag.pubads().setTargeting('cmp_value', window.cmp_value);
        if (window.cmp_value === '0') {
            googletag.pubads().setPrivacySettings({nonPersonalizedAds: true})
        }
    }

    private async lazyAds(): Promise<void> {
        if (this.resources.length === 0) {
            document.removeEventListener("scroll", this.scrollEvent);
            return;
        }
        let item = await this.lazyInstance.lazy(this.resources);
        if (item !== undefined) {
            const id = this.getId(item);
            this.resources = this.resources.filter((el) => {
                return el !== item;
            });
            await this.refreshSlot(this.getId(item), true);
            this.executions[id] = 1;
            await this.createTimer(this.getId(item), item);
        }
    }

    public async refreshSlot(id: string, addService: boolean): Promise<void> {
        window.alpred.hb.cmd.push(function () {
            const targetSlot = Object.values(window.adslots).find(
                (slot) => slot.getSlotId().getDomId() === id
            );

            if (targetSlot) {
                if (addService) {
                    targetSlot.addService(googletag.pubads());
                }
                window.alpred.hb.refresh([targetSlot]);
            }
        });
    }

    public async reLaunch(contenido: HTMLElement, id_noticia: number, page: number): Promise<void> {
        const ads = await window.slots;
        this.resources = Array.from(contenido.querySelectorAll('[class*="publicidad"]')).map(item => {
            return item as HTMLElement;
        }).filter(item => {
            const chunks = this.getId(item).split("-");
            return ads.includes(chunks[0]+"-"+chunks[1]);
        });
        const slots = window.originalAdSlots;
        for (const [k, slot] of Object.entries(slots)) {
            const sizes = slot.getSizes();
            const adSizes: googletag.MultiSize = [];
            sizes.forEach(size => {
                // @ts-ignore
                adSizes.push([size.width, size.height]);
            });
            const adUnitPath = slot.getAdUnitPath();
            const newDivId = slot.getSlotId().getDomId() + "-" + id_noticia;
            const newSlot = googletag.defineSlot(adUnitPath, adSizes, newDivId);
            newSlot?.setTargeting("scroll_infinito", page.toString());
            // @ts-ignore
            window.adslots[newSlot.getSlotId().getDomId()] = newSlot;
        }
        document.addEventListener("scroll", this.scrollEvent);
    }

    public static reset(key: string, adNodes: HTMLElement[], resetNodes: string[]): void {
        if (resetNodes.length > 0) PublicidadController.instance.resetAds(key, adNodes, resetNodes);
    }

    private resetAds(key: string, adNodes: HTMLElement[], resetNodes: string[]): void {
        this.resources = this.resources.filter((item) => {
            return !resetNodes.includes(item.id);
        });
        for (let node of adNodes) this.resources.push(node);
    }

    private async whenRenderFinish(): Promise<void> {
        window.googletag.pubads().addEventListener("slotRenderEnded", function (event: any) {
            const slot = event.slot;
            const sizes = slot.getSizes();

            let minHeight = Number.MAX_SAFE_INTEGER;
            let maxHeight = Number.MIN_SAFE_INTEGER;
            let height = 0;

            for (const size of sizes) {
                if (size.width <= 1 || size.height <= 1) continue;
                height = size.height;
                minHeight = Math.min(minHeight, height);
                maxHeight = Math.max(maxHeight, height);
            }

            const adUnitId = slot.getSlotId().getDomId();
            const contenedorPadre = document.querySelector("#publicidad-" + adUnitId) as HTMLElement;
            const creatividad = document.querySelector("#" + adUnitId) as HTMLElement;
            const googleContainerAd = creatividad.firstElementChild as HTMLElement;

            if (contenedorPadre && creatividad && googleContainerAd) {
                setTimeout(() => {
                    const altura = googleContainerAd.clientHeight < minHeight ? minHeight : googleContainerAd.clientHeight > maxHeight ? maxHeight : googleContainerAd.clientHeight;
                    contenedorPadre.style.setProperty('height', `${altura}px`, 'important');
                    creatividad.style.setProperty('height', `${altura}px`, 'important');
                }, 500);
            }

        });
    }

    private loadInterstitial(): void {
        const interstitialSlot = googletag.defineOutOfPageSlot("/151127700/interstitial", googletag.enums.OutOfPageFormat.INTERSTITIAL);
        if (interstitialSlot) {
            const config = {
                interstitial: {
                    triggers: {
                        unhideWindow: true,
                        navBar: true,
                        inactivity: true,
                        rapidScroll: true,
                        endOfArticle: true,
                    },
                },
            };
            interstitialSlot.setConfig(config);
            interstitialSlot.addService(googletag.pubads());
            window.googletag.pubads().refresh([interstitialSlot]);
        }
    }

    public static async loadAdBlockingRecovery(): Promise<void> {
        return new Promise<void>((resolve: any, reject: any) => {
            const s1 = document.createElement('script');
            const s2 = document.createElement('script');
            s1.async = true;
            s1.nonce = "5IxSMv1B-kapHTZ__MwyFg";
            s1.src = "https://fundingchoicesmessages.google.com/i/pub-6423943295703931?ers=1";
            s2.nonce = "5IxSMv1B-kapHTZ__MwyFg";
            s2.innerHTML = "(function() {function signalGooglefcPresent() {if (!window.frames['googlefcPresent']) {if (document.body) {const iframe = document.createElement('iframe'); iframe.style = 'width: 0; height: 0; border: none; z-index: -1000; left: -1000px; top: -1000px;'; iframe.style.display = 'none'; iframe.name = 'googlefcPresent'; document.body.appendChild(iframe);} else {setTimeout(signalGooglefcPresent, 0);}}}signalGooglefcPresent();})();";

            s1.addEventListener('load', resolve);
            s1.addEventListener('error', reject);
            s2.addEventListener('load', resolve);
            s2.addEventListener('error', reject);

            document.head.appendChild(s1);
            document.head.appendChild(s2);
        });
    }

}
